import React, { useState } from 'react';
import Datetime from 'react-datetime';
import { Global } from '@emotion/core';
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
  Path,
} from 'react-hook-form';
import {
  Type,
  IconChevronDown,
  ButtonPrimary,
  ButtonSecondary,
  spacer8,
} from '@spotify-internal/encore-web';
import { Modal } from '@spotify-internal/anchor-ds';
import { useT } from 'client/i18n/hooks/useT';
import { useDateTimeFormatter } from 'client/i18n/hooks/useDateTimeFormatter';
import { useLocale } from 'client/i18n/hooks/useLocale';
import {
  Container,
  DatetimeGlobalStyles,
  SquareButtonSecondary,
} from './styles';
import { roundToNextHour } from './utils';

export const ControlledFieldDatePicker = <T extends FieldValues>({
  label,
  name = 'publishOn' as Path<T>,
  className,
  control,
}: {
  label: string;
  name: FieldPath<T>;
  className?: any;
  control?: Control<T>;
}) => {
  const t = useT();
  const { locale } = useLocale();
  const dateFormatter = useDateTimeFormatter({
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
  const timeFormatter = useDateTimeFormatter({
    hour: 'numeric',
    minute: 'numeric',
  });
  const [isShowingSchedulerModal, setIsShowingSchedulerModal] = useState(false);
  const {
    field: { value, onChange },
  } = useController<T, FieldPath<T>>({ control, name });
  const [valueOnOpen, setValueOnOpen] = useState(value);

  const dateTimeValue = !value || value === '' ? Date.now() : value;

  return (
    <Container className={className}>
      <Type
        as="span"
        weight="bold"
        aria-label={name}
        style={{ marginBottom: spacer8 }}
      >
        {label}
      </Type>
      <SquareButtonSecondary
        onClick={() => {
          setIsShowingSchedulerModal(true);
          setValueOnOpen(value);
        }}
        iconTrailing={IconChevronDown}
        type="button" // Don't want button to submit form
      >
        {value
          ? t(
              'FieldDatePicker.ScheduleForButtonLabel',
              '{date} at {time}',
              'Epsiode publication scheduled for label',
              {
                date: dateFormatter.format(new Date(value)),
                time: timeFormatter.format(new Date(value)),
              }
            )
          : t('FieldDatePicker.NowLabel', 'Now', 'Episode publish now label')}
      </SquareButtonSecondary>
      <Modal
        size="small"
        aria-labelledby="schedule-publish-title"
        isShowing={isShowingSchedulerModal}
        isShowingCloseButton={true}
        onClose={() => {
          onChange(valueOnOpen);
          setIsShowingSchedulerModal(false);
        }}
      >
        <Modal.Title id="schedule-publish-title">
          {value
            ? t(
                'FieldDatePicker.ScheduleFor.Title',
                'Schedule for {date} at {time}',
                'Epsiode publication scheduled for label',
                {
                  date: dateFormatter.format(new Date(value)),
                  time: timeFormatter.format(new Date(value)),
                }
              )
            : t(
                'FieldDatePicker.PublishNowLabel',
                'Publish now',
                'Episode publish now label'
              )}
        </Modal.Title>
        <Modal.Body
          as="div"
          style={{
            width: '100%',
            maxWidth: '300px',
          }}
        >
          <Global styles={DatetimeGlobalStyles} />
          <Datetime
            locale={locale}
            className="DatetimeContainer"
            input={false}
            timeConstraints={{
              minutes: { min: 0, max: 0, step: 60 },
              seconds: { min: 0, max: 0, step: 60 },
              milliseconds: { min: 0, max: 0, step: 1000 },
            }}
            onChange={date => {
              onChange(roundToNextHour(date));
            }}
            value={dateTimeValue as Date}
          />
          {t(
            'FieldDatePicker.LocalTimeZonesMessage',
            'All times are local',
            'Date picker local time zones message'
          )}
        </Modal.Body>
        <Modal.Footer>
          <ButtonSecondary
            onClick={() => {
              onChange(null);
              setIsShowingSchedulerModal(false);
            }}
          >
            {t(
              'FieldDatePicker.ResetToNowButton',
              'Reset to now',
              'Date picker reset to now button text'
            )}
          </ButtonSecondary>
          <ButtonPrimary onClick={() => setIsShowingSchedulerModal(false)}>
            {t('Button.Confirm', 'Confirm', 'Confirm button text')}
          </ButtonPrimary>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
